// Pure helpers for the coin wording editor (asrth/mdf-bo-fn#296; spec
// docs/specs/2026-10-01-coin-terms-design.md §1 "ค่าที่แก้ได้", §3 HTTP, §4 FE).
//
// Consumer: src/views/AppCatalog/StorefrontSettings.vue — the "ถ้อยคำ coin"
// section under the Cash Back card. Behaviour is specified in
// tests/unit/coinTerms.spec.js.
//
// WHY A SEPARATE FILE AT ALL
//
// This repo's jest cannot mount a .vue file (see reference-bo-fn-jest-infra),
// so everything decidable about the editor lives here where a spec can call it
// directly: how long a text may be, what makes it unacceptable, which keys a
// Save is allowed to PUT, and how the customer-facing preview is paragraphed.
// The component only binds and renders.
//
// THREE RULES THIS FILE EXISTS TO KEEP
//
//   1. The text is PLAIN TEXT, never HTML (§1: "ไม่มี HTML (กัน XSS)"). The
//      preview renders paragraphs as real <p> nodes from termsParagraphs() —
//      never v-html — and a `<tag` in the input is refused here as well, the
//      same way the API answers 400 `bad_text`, so the operator learns it
//      before a round trip instead of after one.
//   2. Lengths are counted in CODE POINTS (Array.from), because the API counts
//      Go runes (utf8.RuneCountInString). `"ก".length` and `"😀".length` are 1
//      and 2 in UTF-16, which would let a counter say 300/300 on a note the
//      server rejects, or refuse one it would have accepted.
//   3. A Save PUTs only what changed (§3: "PUT partial"). Sending all six
//      strings back would rewrite `coin_terms_*` with a byte-identical value
//      on every save, and the API bumps `coin_terms_version` whenever the terms
//      text is written differently — a no-op save must not look like a new
//      legal version to the customers who have to accept it.

// The API's limits, mirrored (§1). Duplicated on purpose so the counter under
// each box is honest and the error is instant — but the API stays the
// authority: the section still surfaces a 400 `bad_text` it failed to predict.
export const COIN_TEXT_LIMITS = { name: 32, note: 300, terms: 8000 };

// The six editable strings, in the order the form shows them. `coin_terms_version`
// and `coin_terms_updated_at` are NOT here: they are read-only, owned by the API,
// and must never be part of a PUT body.
export const COIN_TEXT_FIELDS = [
  'coin_name_th',
  'coin_name_en',
  'coin_short_note_th',
  'coin_short_note_en',
  'coin_terms_th',
  'coin_terms_en',
];

// Which limit (and which validation rules) apply to each field.
const COIN_TEXT_KIND = {
  coin_name_th: 'name',
  coin_name_en: 'name',
  coin_short_note_th: 'note',
  coin_short_note_en: 'note',
  coin_terms_th: 'terms',
  coin_terms_en: 'terms',
};

// Error keys returned by validateCoinTexts. They are i18n key SUFFIXES under
// `coin_terms.err.*`, not sentences — the wording is legal copy and belongs in
// th.js / en.js, which is the whole point of this feature.
export const ERR_EMPTY = 'empty';
export const ERR_TOO_LONG = 'too_long';
export const ERR_TAG = 'tag';
export const ERR_NOT_TEXT = 'not_text';

// coinTextLimitFor answers the limit for one field name, or 0 for a name this
// feature does not own (so a caller cannot accidentally validate
// `coin_terms_version` as if it were text).
export function coinTextLimitFor(field) {
  const kind = COIN_TEXT_KIND[field];
  return kind ? COIN_TEXT_LIMITS[kind] : 0;
}

// coinTextLength counts what the API counts: code points, not UTF-16 units.
// Anything that is not a string has no length — the counter shows 0 rather
// than crashing on a null the first GET has not filled in yet.
export function coinTextLength(v) {
  if (typeof v !== 'string') return 0;
  return Array.from(v).length;
}

// normalizeCoinText is the value that actually gets PUT: CRLF/CR collapsed to
// LF, then trimmed.
//
// Both halves matter. A textarea can hand over \r\n (and a value pasted from
// Word certainly will), which would make an untouched box look edited and,
// worse, would count two code points per line break against the 8,000 limit.
// And leading/trailing whitespace is meaningful in none of the six fields,
// while a stray trailing newline on the terms box is the easiest way to bump a
// legal version number by accident.
//
// Interior line breaks are preserved exactly as typed — every one of them is a
// paragraph break per §1, so collapsing or adding any would rewrite the document.
export function normalizeCoinText(v) {
  if (typeof v !== 'string') return v;
  return v.replace(/\r\n?/g, '\n').trim();
}

// hasMarkupTag spots the thing the API refuses: a `<` that opens something.
//
// It is deliberately narrower than "contains < or >". The terms text is a
// commercial document and will legitimately say things like "ไม่เกิน 50% ของ
// ยอด" or "a < b"; what must never appear is a tag, because the customer-facing
// apps render this text and one <script> that slipped through a future v-html
// would be stored XSS. So: `<` followed by a letter (an opening tag) or by `/`
// (a closing tag) — nothing else.
function hasMarkupTag(s) {
  return /<[a-zA-Z]/.test(s) || /<\//.test(s);
}

// validateCoinTexts answers "would the API accept this form?" as a map of
// field -> error key. An empty object means the form is submittable.
//
// Only the six known fields are examined, and only those PRESENT in the object:
// the form binds all six, but a caller validating a partial patch (or a
// response that an older API did not fill in) must not be told that the keys it
// never sent are empty. null and undefined are both "absent" — el-input yields
// '' when cleared, so a null here means "nothing was loaded", not "cleared".
//
// Per field, in this order:
//   not_text  — present but not a string (a half-deserialised response);
//   empty     — the two NAMES only, after trimming (§1: "ไม่ว่าง"). A note or
//               a terms text MAY be emptied: clearing it is how an operator
//               removes wording they do not want shown;
//   too_long  — more code points than the limit, measured on the NORMALISED
//               value, i.e. on the bytes a Save would really send;
//   tag       — contains markup, checked on the RAW value: a tag hidden in
//               trailing whitespace is still a tag.
export function validateCoinTexts(fields) {
  const f = fields || {};
  const errors = {};

  for (let i = 0; i < COIN_TEXT_FIELDS.length; i++) {
    const field = COIN_TEXT_FIELDS[i];
    if (!Object.prototype.hasOwnProperty.call(f, field)) continue;
    const raw = f[field];
    if (raw === null || raw === undefined) continue;
    if (typeof raw !== 'string') {
      errors[field] = ERR_NOT_TEXT;
      continue;
    }
    const value = normalizeCoinText(raw);
    if (COIN_TEXT_KIND[field] === 'name' && value === '') {
      errors[field] = ERR_EMPTY;
      continue;
    }
    if (coinTextLength(value) > coinTextLimitFor(field)) {
      errors[field] = ERR_TOO_LONG;
      continue;
    }
    if (hasMarkupTag(raw)) {
      errors[field] = ERR_TAG;
    }
  }

  return errors;
}

// termsParagraphs splits the full terms into the paragraphs the customer sees
// (§1: "plain text, ขึ้นบรรทัดใหม่ 1 ครั้ง = ย่อหน้าใหม่").
//
// ONE RULE, THREE REPOS. This is byte-for-byte the rule www's
// src/lib/coinTerms.ts and member-fn's src/utils/coinTerms.js apply
// (asrth/mdf-bo-api#694 item 2): split on EVERY newline, CRLF normalised first,
// each paragraph trimmed, empties dropped. The three used to disagree — www on
// any newline, the other two on blank lines only — which meant this BO preview
// could show nine clauses where the storefront showed one, and the preview
// exists precisely to be believed. A preview that paragraphs differently from
// the customer's screen is worse than no preview.
//
// Consequences of "any newline", both wanted:
//   * a run of blank lines is still ONE break, because the empties in between
//     are dropped — "a\n\n\nb" is two paragraphs, not four;
//   * a single newline INSIDE what the author thought was one clause does start
//     a new <p>. That is the cost of matching the storefront, and the editor is
//     told so by the help text under the box.
export function termsParagraphs(text) {
  if (typeof text !== 'string') return [];
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(function (p) {
      return p.trim();
    })
    .filter(function (p) {
      return p !== '';
    });
}

// COIN_PLACEHOLDER_DEFAULTS — what the preview shows when the API told us
// nothing.
//
// The stored terms text carries `{months}` and `{max_use}` tokens; the API
// substitutes them from `coin_expiry_months` / `coin_max_use_percent` before it
// serves the PUBLIC GET /coins/terms, and reports the two numbers back to the BO
// as `placeholders` alongside the RAW templates (#694 item 1). These are the
// coin system's own defaults, used only when `placeholders` is absent — an API
// that predates #694, or a response that lost the key. They are a display
// fallback, never a value this form sends anywhere.
export const COIN_PLACEHOLDER_DEFAULTS = { months: 12, max_use: 50 };

// The tokens this feature owns, mapped to their key in the `placeholders` object.
const COIN_PLACEHOLDER_KEYS = ['months', 'max_use'];

// renderCoinPlaceholders substitutes the two known tokens so the preview reads
// as the customer's screen will.
//
// WHY THE BO MUST SUBSTITUTE AT ALL. The operator edits a TEMPLATE ("มีอายุ
// {months} เดือน"); the customer is served the rendered sentence. Previewing the
// template verbatim would show the operator a `{months}` no customer ever sees,
// and — worse — would hide the one mistake this preview could catch, a token
// misspelt as `{month}` that will ship as literal braces in a legal document.
//
// An UNKNOWN token is left exactly as it was written, for that reason: the
// preview must not quietly repair or blank it. `{month}` stays `{month}` and the
// operator sees the brace in the rendered pane.
//
// A missing / non-object `placeholders`, or a value that is not a finite number,
// falls back to COIN_PLACEHOLDER_DEFAULTS — the preview stays readable instead
// of printing "undefined" into the middle of a clause.
export function renderCoinPlaceholders(text, placeholders) {
  if (typeof text !== 'string') return '';
  const p = placeholders && typeof placeholders === 'object' && !Array.isArray(placeholders)
    ? placeholders
    : {};
  let out = text;
  for (let i = 0; i < COIN_PLACEHOLDER_KEYS.length; i++) {
    const key = COIN_PLACEHOLDER_KEYS[i];
    const raw = p[key];
    let value = COIN_PLACEHOLDER_DEFAULTS[key];
    if (typeof raw === 'number' && isFinite(raw)) {
      value = raw;
    } else if (typeof raw === 'string' && raw.trim() !== '' && isFinite(Number(raw))) {
      // The settings endpoint has shipped quoted numbers before now; a string
      // that IS a number is the API's answer, not a reason to use the default.
      value = Number(raw);
    }
    // split/join, not a RegExp: the token is a literal and every occurrence is
    // replaced (String.replace with a string needle would only take the first).
    out = out.split('{' + key + '}').join(String(value));
  }
  return out;
}

// coinTextsDiff builds the PUT body: only the keys whose text actually changed.
//
// `saved` is the snapshot of what the API last reported, `edited` is what the
// form binds to. Both sides are normalised before comparing, so re-saving an
// untouched form sends {} — which the section reports as "no change" instead of
// bumping the terms version (rule 3 at the top of this file).
//
// '' and undefined are NOT the same thing, and the distinction is the reason
// this function is not a one-liner:
//   * `edited` missing a key means "this form does not edit that field" — it is
//     never sent, whatever `saved` holds;
//   * `edited[key] === ''` against a `saved` value that is absent IS a change:
//     the operator cleared a box the API had never stored, and '' is how you
//     tell the API to store "no wording here". Comparing with a loose == or
//     substituting '' for a missing saved value would swallow it.
// A non-string edited value is not sent at all: there is no PUT that could
// carry it, and coercing it would invent text nobody typed (validateCoinTexts
// reports it as `not_text` so the operator sees why nothing moved).
export function coinTextsDiff(saved, edited) {
  const before = saved || {};
  const after = edited || {};
  const diff = {};

  for (let i = 0; i < COIN_TEXT_FIELDS.length; i++) {
    const field = COIN_TEXT_FIELDS[i];
    if (!Object.prototype.hasOwnProperty.call(after, field)) continue;
    const next = after[field];
    if (typeof next !== 'string') continue;
    const value = normalizeCoinText(next);
    const was = Object.prototype.hasOwnProperty.call(before, field) ? before[field] : undefined;
    if (typeof was === 'string' && normalizeCoinText(was) === value) continue;
    diff[field] = value;
  }

  return diff;
}

// coinTermsUpdatedText renders `coin_terms_updated_at` for the read-only
// "เวอร์ชัน N · แก้ล่าสุด <date>" line.
//
// It SLICES the ISO string instead of parsing it into a Date, for the same
// reason coinExpiresText in memberCoins.js does: the API sends wall-clock
// Bangkok time and a browser in another zone would print a different minute —
// or a different day — for the moment the legal text was changed. Shipped bugs
// in this repo follow that exact shape (see fix-wms-date-literal-z).
//
// Returns 'YYYY-MM-DD HH:mm', or '' for anything unusable (never seeded, zero
// date, junk) so the caller can fall back to a "never edited" sentence.
export function coinTermsUpdatedText(v) {
  if (!v) return '';
  const s = String(v).trim();
  const day = s.substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  if (day === '0001-01-01' || day === '0000-00-00') return '';
  const time = s.substring(11, 16);
  return /^\d{2}:\d{2}$/.test(time) ? day + ' ' + time : day;
}

export default validateCoinTexts;
